import { StepType } from '@reactour/tour';
import TourStepContent from '../TourStepContent';
import { TourConfig } from '../tourTypes';
import { blockAllNoDim, blockInteraction, sidebarMenuStep } from '../stepStyles';

// Step indices for the CANpark "Monitor your site" tour. The gate steps
// (OPEN_CANPARK, MANAGE_SITES_ITEM, VIEW_BUTTON) auto-advance via
// useSidebarTourControl + useMonitorSiteTourSync; the rest are Next-driven.
// A read-only tour: it never changes anything, so no sandbox is needed.
export const CANPARK_MONITOR_STEP = {
	WELCOME: 0,
	OPEN_CANPARK: 1,
	MANAGE_SITES_ITEM: 2,
	VIEW_BUTTON: 3,
	MONITOR_INTRO: 4,
	FILTER: 5,
	VIEWS: 6,
	FINISH: 7,
} as const;

const steps: StepType[] = [
	{
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				title="Monitor your site 📊"
				body="Let’s take a look at how you keep an eye on a parking site in real time. You will see its records, history and stats."
			/>
		),
	},
	{
		selector: '[data-tour="canpark-menu-group"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="canpark-menu-group"]'),
		content: (
			<TourStepContent
				awaitAction
				tailPosition="right"
				body="Open the CANpark menu — click it to expand."
			/>
		),
	},
	{
		selector: '[data-tour="manage-sites-menu-item"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="manage-sites-menu-item"]'),
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="right"
				body="Now click Manage Sites."
			/>
		),
	},
	{
		selector: '[data-tour="view-site-button"]',
		// Left-align the popover to the (small, left-sitting) View button and sit it
		// above, so the tail can point down onto the button instead of centering the
		// wide popover far to its right.
		position: ({ left, top }, prevRect) => [
			Math.max(8, left),
			Math.max(8, top - prevRect.height - 12),
		],
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="top"
				tailOffset={-56}
				body="Click “View” on a parking site to open its monitoring dashboard."
			/>
		),
	},
	{
		selector: '[data-tour="monitor-header"]',
		position: 'bottom',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				tailPosition="bottom"
				hideBack
				body="This is the site’s monitoring dashboard. Its parking records, history and stats all live in this one place."
			/>
		),
	},
	{
		selector: '[data-tour="monitor-filter"]',
		// The filter bar spans the full width but its controls sit on the right;
		// right-align the popover under them so the tail points at the actual
		// controls, not the empty left half of the bar.
		position: ({ right, bottom }, prevRect) => [
			Math.max(8, right - prevRect.width - 16),
			bottom + 12,
		],
		styles: blockInteraction,
		content: (
			<TourStepContent
				tailPosition="bottom"
				body="Filter the records by plate, record type, or a date range and find exactly what you’re looking for."
			/>
		),
	},
	{
		selector: '[data-tour="monitor-tabs"]',
		position: 'bottom',
		styles: blockInteraction,
		content: (
			<TourStepContent
				tailPosition="bottom"
				body="Switch between Grid, Table and Charts to view the records the way that suits you."
			/>
		),
	},
	{
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				finalStep
				title="You’re all set!"
				body="That’s how you monitor a parking site in CANpark. Records, history and stats at a glance."
			/>
		),
	},
];

export const canparkMonitorSiteTour: TourConfig = {
	steps,
	startRoute: '/',
	allowedRoutes: ['/', '/canpark-admin'],
};
